/**
 * Which task controls a task offers, and to whom -- the rules
 * `TaskControlActions` (the task and robot drawers' controls) draws by,
 * kept in a module of their own so a second surface (the phone
 * calendar's sheet, `calendar/PhoneTaskCancel.tsx`) asks exactly the same
 * questions and cannot drift from them.
 */
import { useQuery } from '@tanstack/react-query';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import type { useTaskControl } from '../../api/hooks';
import { useRobots } from '../../api/hooks';
import { useLifts } from '../../api/hooks/useLifts';
import {
  FIRE_RELOCALIZE_ISSUE,
  commissionKey,
  useRobotCommissionStates,
} from '../../api/hooks/useCommissionRobot';
import { useSafeSiteId } from '../../state/useActiveSite';
import { capabilitiesQueryOptions } from '../../api/query-options';
import type { CapabilityId, SiteId, TaskControlRequest } from '../../api/v1';
import type { RoleToken } from '../../auth/types';
import type { RobotStateView, TaskState } from '../../api/rmf-client';
import type { LiftViewModel } from '../../api/adapters';
import { heldLift, isTerminalTaskStatus, robotHoldsTask } from './task-ownership';
import {
  TASK_VERB_LABEL,
  refusedCommandFromError,
  taskCommandOutcome,
  verdictFromError,
  type TaskCommandOutcome,
} from './task-command-verdict';

/**
 * Control actions offered wherever a task can be acted on (the Task
 * detail drawer, and the Robot detail drawer's embedded "current
 * task" view — plan §Phase 4E "showing all meaningfully useful info
 * from the task panel so that the task can be interrupted / cancelled
 * ... easily"). Mapped to the capability that gates them (plan §7.2,
 * §Safety and authorization).
 *
 * `skip_phase`/`undo_phase`/`rewind` are omitted — all three take a
 * REQUIRED `phase_id` target (`TaskRewindRequest.phase_id: conint(ge=0)`
 * upstream, not optional) and there is no phase-selector UI to pick
 * one yet. A real phase-selector is a follow-up before any of these
 * three come back.
 *
 * Cancel vs. kill are NOT redundant — confirmed from RMF's own
 * `Task::cancel()`/`Task::kill()` doc comments (`rmf_task/include/
 * rmf_task/Task.hpp`, reference-only): cancel lets the robot finish
 * whatever phases it needs to return to an unencumbered state first
 * (e.g. set down a payload, back out of a doorway) before stopping;
 * kill forces an immediate stop "even if it remains encumbered by
 * something related to this task" and explicitly supersedes a
 * pending cancel. Kill is the deliberate escalation for exactly the
 * "cancel isn't taking effect" case (confirmed live earlier this
 * session with a stuck interrupted task) — not a duplicate button.
 */
export const CONTROL_ACTIONS: ReadonlyArray<{
  action: TaskControlRequest['action'];
  label: string;
  capability: CapabilityId;
  appliesTo: (status: TaskState['status'], ctx: { held: boolean }) => boolean;
  variant: 'default' | 'danger';
  hint: string;
}> = [
  {
    action: 'interrupt',
    label: 'Interrupt',
    capability: 'task.interrupt',
    appliesTo: (s) => s === 'active',
    variant: 'default',
    hint: 'Pause the task — the robot holds position until resumed.',
  },
  {
    action: 'resume',
    label: 'Resume',
    capability: 'task.resume',
    appliesTo: (s) => s === 'interrupted',
    variant: 'default',
    hint: 'Continue the task from where it was interrupted.',
  },
  {
    action: 'cancel',
    label: 'Cancel Task',
    capability: 'task.cancel',
    // OWNERSHIP AS WELL AS STATUS. On 2026-09-15 a task RMF was still
    // driving projected as `failed`, so this predicate hid Cancel for
    // 47 minutes -- the one lever that would have ended the wedge. A
    // task a robot is provably still executing gets its controls back
    // whatever the record says.
    appliesTo: (s, ctx) =>
      s === 'queued' || s === 'active' || s === 'interrupted' || ctx.held,
    variant: 'danger',
    hint: 'Graceful stop — the robot may finish a few phases first (e.g. set down a payload) before returning to idle.',
  },
  {
    action: 'kill',
    label: 'Kill',
    capability: 'task.kill',
    appliesTo: (s, ctx) =>
      s === 'queued' || s === 'active' || s === 'interrupted' || ctx.held,
    variant: 'danger',
    hint: 'Immediate stop, overriding any pending cancel — the robot may be left encumbered (e.g. still holding a payload). Use when Cancel isn’t taking effect.',
  },
];

/** The roles whose task controls are drawn at all (the backend's
 *  capability table is the authority; this only decides what is drawn). */
export const TASK_CONTROL_ROLES: RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
];

export type TaskControlAction = (typeof CONTROL_ACTIONS)[number];

export interface TaskControlGates {
  /** The controls this task's status (and ownership) admits, before any
   *  lift guard. */
  availableActions: TaskControlAction[];
  capabilityMap: Map<CapabilityId, { enabled: boolean; disabled_reasons: string[] }>;
  /** The capability matrix has arrived: a control whose capability reads
   *  disabled is drawn disabled only once it has. */
  capabilitiesLoaded: boolean;
  robot: RobotStateView | undefined;
  held: boolean;
  inLift: LiftViewModel | null | undefined;
  heldAfterFire: boolean;
  fireHeldBanner: boolean;
  /** Cancel and kill are withheld: the robot is in or boarding a car. */
  guardedForLift: boolean;
  heldTerminalOutsideLift: boolean;
  audience: ReturnType<typeof audienceFor>;
}

/**
 * Every rule that decides which controls a task offers, in one place, so
 * a second surface (the phone calendar's sheet) asks exactly what this
 * component asks and cannot drift from it.
 */
export function useTaskControlGates(
  task: TaskState,
  robotProp?: RobotStateView,
  liftsProp?: readonly LiftViewModel[],
): TaskControlGates {
  const siteId = useSafeSiteId();
  const capabilities = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const capabilityMap = new Map(
    (capabilities.data?.capabilities ?? []).map((c) => [c.capability, c] as const),
  );
  // -- WS3: ownership, and whether the robot is in a car ---------------
  //
  // Both questions are asked on every render, because both answers can
  // change under the operator: a robot enters a cabin in the second
  // between the drawer opening and the press, and the controls have to
  // follow it. `useRobots`/`useLifts` are the same queries the screens
  // already run, so this costs a cache read, not a fetch.
  const robotsQuery = useRobots();
  const liftsQuery = useLifts();
  const robot =
    robotProp ??
    (task.assignedRobot
      ? (robotsQuery.data ?? []).find((r) => r.name === task.assignedRobot)
      : undefined);
  const lifts = liftsProp ?? liftsQuery.data ?? [];
  const held = robotHoldsTask(task, robot, Date.now());
  const inLift = heldLift(robot, lifts);
  // Who reads the in-lift banner below. `audienceFor`, never a list of
  // our own: a mixed account reads ops, and `service_operator` reads
  // site (amendment A2).
  const audience = audienceFor(useAuth().roles);

  // -- Held after a fire alarm ------------------------------------------
  //
  // The Gausium adapter holds a robot that was mid-ride when an alarm
  // started until a person confirms which floor it is on, and reports
  // that hold as a robot ISSUE on the fleet registry row -- the same
  // source `RobotDetailDrawer` reads for its "Held after fire alarm"
  // section. Nothing on the task says so: the task reads `interrupted`,
  // which is exactly the status `resume` applies to, so the drawer
  // offered a Resume that could only ever 409 (sg-nomad-01, 2026-09-16:
  // every press refused, and the one control that does release the
  // robot lives in the Robot drawer, which nothing pointed at).
  //
  // Keyed fleet/robot, not by name (see `commissionKey`). The task
  // record carries no fleet, so the robot row -- which does -- is the
  // source, and a task whose robot cannot be found is simply not held.
  const commissionStates = useRobotCommissionStates();
  const heldAfterFire = Boolean(
    robot &&
      (commissionStates.data?.get(commissionKey(robot.fleet, robot.name))?.issues ?? []).includes(
        FIRE_RELOCALIZE_ISSUE,
      ),
  );
  // Only for a task the hold can be blocking. A terminal record nobody
  // is executing has nothing to release, and "Resume cannot release
  // this task" under a completed task would be a false alarm.
  const fireHeldBanner = heldAfterFire && (!isTerminalTaskStatus(task.status) || held);

  const availableActions = CONTROL_ACTIONS.filter(
    (a) => a.appliesTo(task.status, { held }) && !(a.action === 'resume' && heldAfterFire),
  );

  // WITHHOLD CANCEL AND KILL WHEN THE ROBOT IS IN A CAR, and point at the
  // procedure instead. A task cancelled in a car leaves the robot parked
  // inside holding the lift, and the only safe way out is to push it onto
  // the landing -- `end-held` refuses the same case server-side, so a
  // button here would be a button the API declines.
  //
  // ONLY for a task that needs the attention this is about: an attention
  // task, or a terminal record a robot is provably still executing. A
  // PLAIN HEALTHY ACTIVE TASK KEEPS TODAY'S BUTTONS (action plan §3e) --
  // withholding them from every robot that happens to be riding would
  // take the ordinary Cancel away from ordinary work.
  const guardedForLift = Boolean(
    (task.attention || (held && isTerminalTaskStatus(task.status))) && inLift,
  );
  const heldTerminalOutsideLift = held && isTerminalTaskStatus(task.status) && !inLift;
  return {
    availableActions: [...availableActions],
    capabilityMap: capabilityMap as TaskControlGates['capabilityMap'],
    capabilitiesLoaded: capabilities.data !== undefined,
    robot,
    held,
    inLift,
    heldAfterFire,
    fireHeldBanner,
    guardedForLift,
    heldTerminalOutsideLift,
    audience,
  };
}

/**
 * What the last control verb on `taskId` came to, or null when the last
 * one sent was for another task (see the note in `TaskControlActions`).
 * Shared with the phone calendar's sheet so a cancel reads the same in
 * both places.
 */
export function taskControlOutcome(
  control: ReturnType<typeof useTaskControl>,
  taskId: string,
): TaskCommandOutcome | null {
  const sentFor = control.variables?.taskId;
  const lastAction = control.variables?.action ?? 'command';
  const verb = TASK_VERB_LABEL[lastAction] ?? lastAction;
  if (sentFor !== taskId) return null;
  if (control.isError) {
    const refused = refusedCommandFromError(control.error);
    return refused ? taskCommandOutcome(lastAction, refused) : verdictFromError(verb, control.error);
  }
  return control.data ? taskCommandOutcome(lastAction, control.data.command) : null;
}

